// =============================================================================
// APPTIME.JS — time spent in Agency Base, per person per day
// =============================================================================
// The app sends a heartbeat about once a minute while its tab is on screen and
// the person has used the mouse or keyboard in the last few minutes. The
// database (track_app_time) only credits time that has really passed, so two
// open tabs or a tampered client can't inflate the total.
//
// Days are Montreal calendar days, matching how the server buckets them.
// =============================================================================

export const HEARTBEAT_MS = 60 * 1000
export const IDLE_AFTER_MS = 5 * 60 * 1000
export const MAX_CREDIT_SECONDS = 120

/** 'YYYY-MM-DD' for a moment, in Montreal time. */
export function montrealDay(date = new Date()) {
  return date.toLocaleDateString('en-CA', { timeZone: 'America/Montreal' })
}

function addDays(dayStr, n) {
  const d = new Date(`${dayStr}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** Monday of the week containing dayStr. */
export function weekStartDay(dayStr) {
  const dow = new Date(`${dayStr}T12:00:00Z`).getUTCDay() // 0 = Sunday
  return addDays(dayStr, -((dow + 6) % 7))
}

/**
 * Seconds to credit for this heartbeat. Nothing when the tab is hidden or the
 * person has been idle; otherwise the time since the previous beat, capped.
 */
export function secondsToCredit({ now, lastBeatAt, lastActivityAt, visible }) {
  if (!visible) return 0
  if (now - lastActivityAt > IDLE_AFTER_MS) return 0
  const elapsed = Math.round((now - lastBeatAt) / 1000)
  return Math.max(0, Math.min(elapsed, MAX_CREDIT_SECONDS))
}

/** Per-user totals: today, this week (Mon–), this month, last 30 days. */
export function summarizeAppTime(rows, today = montrealDay()) {
  const week = weekStartDay(today)
  const month = today.slice(0, 8) + '01'
  const from30 = addDays(today, -29)
  const out = {}
  for (const r of rows || []) {
    const u = out[r.user_id] || (out[r.user_id] = { today: 0, week: 0, month: 0, d30: 0, days30: 0, lastSeen: null })
    const s = r.seconds || 0
    if (r.day === today) u.today += s
    if (r.day >= week && r.day <= today) u.week += s
    if (r.day >= month && r.day <= today) u.month += s
    if (r.day >= from30 && r.day <= today) { u.d30 += s; if (s > 0) u.days30++ }
    if (r.last_seen_at && (!u.lastSeen || r.last_seen_at > u.lastSeen)) u.lastSeen = r.last_seen_at
  }
  return out
}

/** '0m', '45m', '2h 05m'. */
export function formatDuration(seconds) {
  const mins = Math.floor((seconds || 0) / 60)
  if (mins < 60) return `${mins}m`
  const h = Math.floor(mins / 60)
  return `${h}h ${String(mins % 60).padStart(2, '0')}m`
}
